import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { authAPI } from '../../lib/api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { toast } from 'sonner';
import { Loader2, ArrowLeft, Building2, CheckCircle2 } from 'lucide-react';

const RegisterPage = () => {
  const [searchParams] = useSearchParams();
  const inviteToken = searchParams.get('invite');
  const [inviteData, setInviteData] = useState(null);
  const [inviteLoading, setInviteLoading] = useState(!!inviteToken);
  const [inviteError, setInviteError] = useState(false);

  const [formData, setFormData] = useState({
    prenom: '',
    nom: '',
    email: '',
    password: '',
    confirmPassword: '',
  });
  const [loading, setLoading] = useState(false);
  const { register } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (inviteToken) {
      authAPI.validateInvite(inviteToken)
        .then(res => {
          setInviteData(res.data);
          setFormData(prev => ({ ...prev, email: res.data.email }));
        })
        .catch(() => setInviteError(true))
        .finally(() => setInviteLoading(false));
    }
  }, [inviteToken]);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (formData.password !== formData.confirmPassword) {
      toast.error('Les mots de passe ne correspondent pas');
      return;
    }

    if (formData.password.length < 8) {
      toast.error('Le mot de passe doit contenir au moins 8 caracteres');
      return;
    }

    setLoading(true);

    try {
      await register({
        email: formData.email,
        password: formData.password,
        nom: formData.nom,
        prenom: formData.prenom,
        role: 'client',
      }, inviteToken || undefined);
      toast.success('Compte cree avec succes!');
      navigate('/portail');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la creation du compte');
    } finally {
      setLoading(false);
    }
  };

  if (inviteLoading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-[#F9B438]" />
      </div>
    );
  }

  if (inviteError) {
    return (
      <div className="min-h-screen bg-slate-50 flex flex-col items-center justify-center p-4">
        <Card className="w-full max-w-md border-slate-200 text-center">
          <CardContent className="py-12">
            <div className="w-16 h-16 rounded-full bg-red-50 flex items-center justify-center mx-auto mb-4">
              <span className="text-red-500 text-2xl">!</span>
            </div>
            <h2 className="text-xl font-bold text-slate-900 mb-2">Invitation invalide</h2>
            <p className="text-slate-500 mb-6">Ce lien d'invitation est invalide ou a expire.</p>
            <Link to="/connexion">
              <Button variant="outline">Se connecter</Button>
            </Link>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col" data-testid="register-page">
      <div className="p-4">
        <Link to="/" className="inline-flex items-center gap-2 text-slate-600 hover:text-primary transition-colors">
          <ArrowLeft className="w-4 h-4" />
          Retour au site
        </Link>
      </div>

      <div className="flex-1 flex items-center justify-center p-4">
        <Card className="w-full max-w-md border-slate-200">
          <CardHeader className="text-center">
            <div className="w-12 h-12 bg-primary rounded-lg flex items-center justify-center mx-auto mb-4">
              <span className="text-white font-heading font-bold text-xl">P</span>
            </div>
            <CardTitle className="font-heading text-2xl">Creer un compte</CardTitle>
            <CardDescription>
              {inviteData
                ? 'Vous avez ete invite a rejoindre le portail client'
                : 'Inscrivez-vous pour acceder a l\'espace client'}
            </CardDescription>
            {inviteData?.client_name && (
              <div className="flex items-center justify-center gap-2 mt-3">
                <Badge variant="outline" className="text-sm px-3 py-1 border-[#F9B438] text-[#1D1D1B] bg-[#F9B438]/10">
                  <Building2 className="w-3.5 h-3.5 mr-1.5" />
                  {inviteData.client_name}
                </Badge>
              </div>
            )}
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="prenom">Prenom</Label>
                  <Input
                    id="prenom"
                    name="prenom"
                    value={formData.prenom}
                    onChange={handleChange}
                    required
                    placeholder="Jean"
                    data-testid="register-prenom"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="nom">Nom</Label>
                  <Input
                    id="nom"
                    name="nom"
                    value={formData.nom}
                    onChange={handleChange}
                    required
                    placeholder="Dupont"
                    data-testid="register-nom"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="email">Courriel</Label>
                <Input
                  id="email"
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  placeholder="vous@exemple.com"
                  readOnly={!!inviteData}
                  className={inviteData ? 'bg-slate-50 text-slate-600' : ''}
                  data-testid="register-email"
                />
                {inviteData && (
                  <p className="text-xs text-emerald-600 flex items-center gap-1">
                    <CheckCircle2 className="w-3 h-3" /> Courriel pre-rempli par l'invitation
                  </p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="password">Mot de passe</Label>
                <Input
                  id="password"
                  name="password"
                  type="password"
                  value={formData.password}
                  onChange={handleChange}
                  required
                  placeholder="********"
                  data-testid="register-password"
                />
                <p className="text-xs text-slate-500">Minimum 8 caracteres</p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="confirmPassword">Confirmer le mot de passe</Label>
                <Input
                  id="confirmPassword"
                  name="confirmPassword"
                  type="password"
                  value={formData.confirmPassword}
                  onChange={handleChange}
                  required
                  placeholder="********"
                  data-testid="register-confirm-password"
                />
              </div>

              <Button
                type="submit"
                className="w-full bg-primary hover:bg-primary/90"
                disabled={loading}
                data-testid="register-submit"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Creation...
                  </>
                ) : (
                  'Creer mon compte'
                )}
              </Button>
            </form>

            <div className="mt-6 text-center text-sm">
              <p className="text-slate-600">
                Deja un compte?{' '}
                <Link to="/connexion" className="text-primary hover:underline font-medium">
                  Se connecter
                </Link>
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default RegisterPage;
